import { useMemo } from "react"
import { rgba2hex } from "./IOS"

export const bgIsLight = (color) => {
  const hex = color.replace("#", "")
  const c_r = parseInt(hex.substr(0, 2), 16)
  const c_g = parseInt(hex.substr(2, 2), 16)
  const c_b = parseInt(hex.substr(4, 2), 16)
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000
  return brightness > 155
}

type TProps = {
  contentWidth: number
  contentHeight: number
  contentRadius: number
  aspectRatio: number
  bezelX: number
  scale: number
  trueScale: number
  backgroundColor: string
}

export type TIosFrameResult = {
  isLightBackground: boolean
  borderWidth: number
  deviceRadius: number
  notchHeight: number
  notchWidth: number
  deviceWidth: number
  deviceHeight: number
  notchXOffset: number
  notchRadius: number
  notchReliefWidth: number
  notchReliefXOffset: number
  modalCornerRadius: number
  modalOffset: number
  modalBackgroundWidth: number
}

const useIosFrame = ({
  contentWidth,
  contentHeight,
  contentRadius,
  aspectRatio,
  bezelX,
  scale,
  trueScale,
  backgroundColor = "black",
}: TProps): TIosFrameResult => {
  return useMemo(() => {
    const isBgHexValue = backgroundColor.startsWith("#") && !backgroundColor.includes("rgba")
    const bgColor = isBgHexValue ? backgroundColor : rgba2hex(backgroundColor)
    const isLightBackground = bgIsLight(bgColor)
    const borderWidth = Math.min(Math.round(contentWidth * 0.03), 12)
    const deviceRadius = (contentRadius + bezelX) * scale
    const notchHeight = contentWidth * 0.083
    const notchWidth = contentWidth * 0.41
    const deviceWidth = contentWidth * trueScale + borderWidth * 2
    const deviceHeight = contentWidth * aspectRatio * trueScale + borderWidth * 2
    const notchXOffset = (contentWidth - notchWidth) / 2
    const notchRadius = notchHeight * 0.65
    const notchReliefWidth = notchHeight - notchRadius
    const notchReliefXOffset = notchXOffset - notchReliefWidth

    const modalCornerRadius = 15
    const modalOffset = (170 / 2532) * contentHeight
    const modalBackgroundWidth = (1075 / 1170) * contentWidth

    return {
      isLightBackground,
      borderWidth,
      deviceRadius,
      notchHeight,
      notchWidth,
      deviceWidth,
      deviceHeight,
      notchXOffset,
      notchRadius,
      notchReliefWidth,
      notchReliefXOffset,
      modalCornerRadius,
      modalOffset,
      modalBackgroundWidth,
    }
  }, [
    backgroundColor,
    contentWidth,
    contentHeight,
    contentRadius,
    aspectRatio,
    bezelX,
    scale,
    trueScale,
  ])
}

export default useIosFrame
